/* eslint-disable @typescript-eslint/no-explicit-any */

import hoistNonReactStatics from 'hoist-non-react-statics'
import React, { Component, type ReactNode } from 'react'
import { log } from '@s4l/monitoring'

interface ErrorInfo {
  componentStack: string
}

interface ErrorBoundaryState {
  hasError: boolean
  error?: Error
}

interface ErrorBoundaryProps {
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: ErrorInfo) => void
  componentName?: string
  children: ReactNode
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { onError, componentName } = this.props

    log.error({
      message: `Component render error caught by ErrorBoundary: ${componentName || 'Unknown'}`,
      metadata: {
        error,
        userAgent: navigator?.userAgent ?? 'Unknown',
      },
    })

    if (onError) {
      onError(error, errorInfo)
    }
  }

  render() {
    const { hasError } = this.state
    const { fallback, children } = this.props

    if (hasError) {
      return fallback || null
    }

    return children
  }
}

interface WithErrorBoundaryOptions {
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: ErrorInfo) => void
}

export function withErrorBoundary<C extends React.ComponentType<any>>(
  WrappedComponent: C,
  options: WithErrorBoundaryOptions = {}
): C {
  const { fallback, onError } = options

  const componentName = WrappedComponent.displayName || WrappedComponent.name || 'Component'

  const WithErrorBoundaryComponent = (props: React.ComponentProps<C>) => (
    <ErrorBoundary fallback={fallback} onError={onError} componentName={componentName}>
      <WrappedComponent {...(props as any)} />
    </ErrorBoundary>
  )

  WithErrorBoundaryComponent.displayName = `withErrorBoundary(${componentName})`

  return hoistNonReactStatics(WithErrorBoundaryComponent, WrappedComponent) as any as C
}
